import React from 'react';
import styled from 'styled-components';
import { BodyLink, Button, mq, NextIcon } from '@kvdbil/components';
import Link from '~/App/shared/components/Link';

const Header = styled.div`
  display: flex;
  height: 4rem;
  align-items: center;

  ${mq(null, 'tablet')} {
    margin-bottom: 1rem;
  }
`;

const Image = styled.img`
  margin-right: 1rem;
  width: 4rem;

  ${mq('tablet')} {
    width: 6rem;
    margin-right: 2rem;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${mq('tablet')} {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Explanation = styled.div`
  display: flex;
  flex-direction: column;

  ${mq('tablet')} {
    flex: 3;
    margin-right: 1.5rem;
  }
`;

const HeadingOne = styled.h2`
  ${mq('tablet')} {
    display: none;
  }
`;

const HeadingTwo = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 0.5rem;

  ${mq(null, 'tablet')} {
    display: none;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0 1rem;

  ${mq('tablet')} {
    margin-top: 0rem;
    flex-direction: column;
    flex: 1;
    display: flex;
    align-items: flex-end;
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  min-height: 2.5rem;
`;

const StepButton = styled(Button)`
  min-width: 9.375rem;
  justify-content: center;
`;

interface Props {
  currentStep: number;
  handleClose: () => void;
  onComplete?: () => void;
  setCurrentStep: (step: number) => void;
  totalSteps: number;
  descriptionTitle?: string | null;
  descriptionBodyText?: string | null;
  buttons: {
    nextButton?: string | null;
    exitButton?: string | null;
    finalButton?: string | null;
  };
  finalLink?: {
    text?: string | null;
    url?: string | null;
  } | null;
  imageIcon?: {
    icon?: { url: string } | null;
  } | null;
}

const Step = ({
  currentStep,
  handleClose,
  onComplete,
  setCurrentStep,
  totalSteps,
  descriptionTitle,
  descriptionBodyText,
  buttons,
  finalLink,
  imageIcon
}: Props) => {
  const isFinalStep = Number(currentStep) + 1 === totalSteps;

  const handleNextStep = () => {
    if (isFinalStep) {
      onComplete?.();
      handleClose();
    } else {
      setCurrentStep(Number(currentStep) + 1);
    }
  };

  return (
    <Wrapper>
      <Header>
        <Image
          src={imageIcon?.icon?.url ?? ''}
          alt={`image-${descriptionTitle}`}
        />
        <HeadingOne>{descriptionTitle}</HeadingOne>
      </Header>
      <Explanation>
        <HeadingTwo>{descriptionTitle}</HeadingTwo>
        <span>{descriptionBodyText}</span>
      </Explanation>
      <Footer>
        {!isFinalStep && (
          <>
            <StepButton
              color="neutral"
              variant="outline"
              onClick={handleNextStep}
            >
              {buttons.nextButton}
            </StepButton>
            <StepButton color="neutral" variant="flat" onClick={handleClose}>
              {buttons.exitButton}
            </StepButton>
          </>
        )}
        {isFinalStep && (
          <>
            <StepButton
              color="neutral"
              variant="outline"
              onClick={handleNextStep}
            >
              {buttons.finalButton}
            </StepButton>
            <StyledLink
              to={finalLink?.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BodyLink as="span">{finalLink?.text}</BodyLink>
              <NextIcon />
            </StyledLink>
          </>
        )}
      </Footer>
    </Wrapper>
  );
};

export default Step;
